import { useEffect, useState } from 'react'
import { tiller, type TillerReport } from './lib/tiller'

const count = new Intl.NumberFormat('en-US')
const sheetUrl = (id: string) => `https://docs.google.com/spreadsheets/d/${id}/edit`

/**
 * The Profile page's Tiller section: link a Tiller sheet, see what it added,
 * unlink it. Nothing here writes to either spreadsheet; the link is kept in
 * the app's hidden Drive folder.
 */
export function TillerLink({ status, onChanged }: { status?: { report?: TillerReport; error?: string }; onChanged: () => Promise<unknown> }) {
  const [linked, setLinked] = useState<string | null>()
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string>()

  useEffect(() => {
    tiller.linkedId().then((id) => setLinked(id ?? null), (e) => setError((e as Error).message))
  }, [])

  const run = async (work: () => Promise<string | null>) => {
    setBusy(true)
    setError(undefined)
    try {
      setLinked(await work())
      await onChanged()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const r = status?.report
  return (
    <section className="section tiller">
      <h2>Tiller sheet</h2>
      <p className="muted small">
        Adds the transactions from a Tiller spreadsheet. Finance Buddy only reads it, never writes to it, and doesn’t copy its rows into your
        spreadsheet. Where a statement you uploaded covers an account on a date, the statement’s transactions are used and Tiller’s are left out.
        Only accounts already in your Accounts tab take Tiller rows. Unlink to go back to statements alone.
      </p>
      {linked === undefined && !error && <p className="muted">Checking…</p>}
      {linked === null && (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            run(async () => (await tiller.link(text)).spreadsheetId)
          }}
        >
          <input type="text" aria-label="Tiller sheet link" placeholder="https://docs.google.com/spreadsheets/d/…" value={text} onChange={(e) => setText(e.target.value)} />
          <button type="submit" disabled={busy || !text.trim()}>{busy ? 'Reading…' : 'Link'}</button>
        </form>
      )}
      {linked && (
        <>
          <div className="row">
            <p>
              ✓ Linked to <a href={sheetUrl(linked)} target="_blank" rel="noreferrer">{r?.title ?? 'your Tiller sheet'}</a>
            </p>
            <button
              className="quiet"
              disabled={busy}
              onClick={() =>
                run(async () => {
                  await tiller.unlink()
                  return null
                })
              }
            >
              {busy ? 'Working…' : 'Unlink'}
            </button>
          </div>
          {status?.error && <p className="error">✕ Couldn’t read it this time: {status.error}. The dashboard is showing statements only.</p>}
          {r && (
            <>
              <dl className="drawer-stats">
                <div><dt className="label">Rows read</dt><dd className="num">{count.format(r.read)}</dd></div>
                <div><dt className="label">Added</dt><dd className="num">{count.format(r.used)}</dd></div>
                <div><dt className="label">Left out: a statement covers them</dt><dd className="num">{count.format(r.covered)}</dd></div>
                <div><dt className="label">Left out: already here from a document</dt><dd className="num">{count.format(r.matched)}</dd></div>
                {r.unreadable > 0 && <div><dt className="label">Left out: no date or amount</dt><dd className="num">{count.format(r.unreadable)}</dd></div>}
              </dl>
              {r.unmatched.length > 0 && (
                <p className="muted small">
                  Left out because they aren’t in your Accounts tab: {r.unmatched.map((u) => `${u.account} (${count.format(u.rows)} rows)`).join(', ')}. Add a row
                  for one in the Accounts tab, with its last 4 digits, to include it.
                </p>
              )}
            </>
          )}
        </>
      )}
      {error && <p className="error">✕ {error}</p>}
    </section>
  )
}
